import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import userService from '../services/UserService';
import { useUserList } from './useUserList';
import { usePagination } from './usePagination';
import {
  createRiskCacheEntryFromModal,
  getExistingUserRiskLevel,
  mergeRiskLevel,
} from '../utils/riskLevelUtils';

const normalizeFavouriteFlag = (value) => {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === '0' || normalized === 'false' || normalized === 'no') {
      return false;
    }
    if (normalized === '1' || normalized === 'true' || normalized === 'yes') {
      return true;
    }
  }

  return Boolean(value);
};

const getFavouriteNotes = (user) =>
  String(
    user?.favouriteNotes ??
      user?.favoriteNotes ??
      user?.favouritesNotes ??
      user?.favouriteDescription ??
      user?.favoriteDescription ??
      user?.favouritesDescription ??
      user?.favouriteComments ??
      user?.favoriteComments ??
      ''
  ).trim();

const formatModels = (models) => {
  if (Array.isArray(models)) {
    return models.filter(Boolean).join(', ') || '—';
  }

  return models || '—';
};

const normalizeFavouriteData = (user) => ({
  isFavourite: normalizeFavouriteFlag(user?.isFavourite ?? user?.isFavourites),
  favouriteNotes: getFavouriteNotes(user),
});

export function useUsersTableData(selectedSiteId, options = {}) {
  const { includeRiskLevel = true } = options;
  const [users, setUsers] = useState([]);
  const [riskCache, setRiskCache] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'lastOnline', direction: 'desc' });
  const [error, setError] = useState(null);
  const [canFetchUsers, setCanFetchUsers] = useState(false);

  const {
    users: userList,
    loading: loadingList,
    error: errorList,
  } = useUserList(searchQuery, selectedSiteId, { enabled: canFetchUsers, delay: 300 });

  const riskCacheRef = useRef(riskCache);

  useEffect(() => {
    riskCacheRef.current = riskCache;
  }, [riskCache]);

  const getCachedRiskEntry = useCallback(
    (user) => {
      const visorId = user?.visorId;
      return visorId ? riskCacheRef.current[visorId] ?? null : null;
    },
    []
  );

  const fetchUsers = useCallback(
    async (showAll = false) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await userService.getUsers(showAll ? '' : searchQuery, selectedSiteId);
        const data = Array.isArray(response) ? response : [];

        setUsers(
          data.map((user) => {
            const cachedRisk = getCachedRiskEntry(user);
            const favouriteData = normalizeFavouriteData(user);
            return {
              ...user,
              ...favouriteData,
              riskLevel: mergeRiskLevel(getExistingUserRiskLevel(user), cachedRisk?.riskLevel),
              riskData: cachedRisk?.riskData ?? user.riskData ?? null,
            };
          })
        );

        if (data.length === 0) {
          setError('No records found');
        }
      } catch (err) {
        setError('No records found.');
      } finally {
        setIsLoading(false);
      }
    },
    [getCachedRiskEntry, searchQuery, selectedSiteId]
  );

  useEffect(() => {
    if (searchQuery.length < 3 || !canFetchUsers) return undefined;

    const delayDebounce = setTimeout(() => {
      fetchUsers(false);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery, canFetchUsers, fetchUsers]);

  const handleSelectUser = useCallback((user) => {
    setSearchQuery(user.username);
    setCanFetchUsers(false);
  }, []);

  const handleSort = useCallback((columnKey) => {
    setSortConfig((prevSortConfig) => {
      const isSameColumn = prevSortConfig.key === columnKey;
      const direction = isSameColumn && prevSortConfig.direction === 'asc' ? 'desc' : 'asc';
      return { key: columnKey, direction };
    });
  }, []);

  const sortedUsers = useMemo(() => {
    const sorted = [...users];

    sorted.sort((a, b) => {
      const { key, direction } = sortConfig;
      const dir = direction === 'asc' ? 1 : -1;

      if (key === 'isFavourite') {
        const valueA = a.isFavourite ? 0 : 1;
        const valueB = b.isFavourite ? 0 : 1;
        return (valueA - valueB) * dir;
      }

      if (key === 'lastOnline' || key === 'installationDate' || key === 'forcedStopDate') {
        const valueA = Date.parse(a[key] ?? '') || -Infinity;
        const valueB = Date.parse(b[key] ?? '') || -Infinity;
        return (valueA - valueB) * dir;
      }

      const valueA = a[key] ?? '';
      const valueB = b[key] ?? '';
      if (valueA < valueB) return -1 * dir;
      if (valueA > valueB) return 1 * dir;
      return 0;
    });

    return sorted;
  }, [sortConfig, users]);

  const {
    paginatedData,
    currentPage,
    totalPages,
    handlePageChange,
    resetToFirstPage,
  } = usePagination(sortedUsers);

  const exportToXlsx = useCallback(async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Users');

      worksheet.columns = [
        { header: 'Group', key: 'Group', width: 20 },
        { header: 'Visor Reference', key: 'VisorReference', width: 18 },
        { header: 'Version', key: 'Version', width: 15 },
        { header: 'OS', key: 'OS', width: 15 },
        { header: 'Clones', key: 'Clones', width: 24 },
        { header: 'Last Online', key: 'LastOnline', width: 20 },
        { header: 'Installation Date', key: 'InstallationDate', width: 20 },
        { header: 'Notifications', key: 'Notifications', width: 20 },
        { header: 'Capture Service', key: 'CaptureService', width: 20 },
        ...(includeRiskLevel ? [{ header: 'Risk Level', key: 'RiskLevel', width: 10 }] : []),
      ];

      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' },
      };

      const data = sortedUsers.map((user) => {
        const row = {
          Group: user.groupName || '—',
          VisorReference: user.visorId,
          Version: user.version || '—',
          OS: user.os ? `Android ${user.os}` : '—',
          Clones: formatModels(user.models),
          LastOnline: user.lastOnline ? new Date(user.lastOnline) : null,
          InstallationDate: user.installationDate ? new Date(user.installationDate) : null,
          Notifications: user.notifications === 'allowed' ? 'Enabled' : 'Disabled',
          CaptureService: user.captureServiceRunning === 'true' ? 'Running' : 'Stopped',
        };

        if (includeRiskLevel) {
          row.RiskLevel = user.riskLevel ?? 0;
        }

        return row;
      });

      worksheet.getColumn('LastOnline').numFmt = 'dd/mm/yyyy hh:mm:ss';
      worksheet.getColumn('InstallationDate').numFmt = 'dd/mm/yyyy hh:mm:ss';
      data.forEach((row) => worksheet.addRow(row));

      worksheet.autoFilter = {
        from: 'A1',
        to: `${worksheet.getColumn(worksheet.columns.length).letter}${data.length + 1}`,
      };

      const buffer = await workbook.xlsx.writeBuffer();
      const stamp = new Date().toISOString().slice(0, 10);
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      saveAs(blob, `device_users_export_${stamp}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export Excel file. Please try again.');
    }
  }, [includeRiskLevel, sortedUsers]);

  const applyFilters = useCallback(() => {
    setCanFetchUsers(false);
    resetToFirstPage();
    fetchUsers(false);
  }, [fetchUsers, resetToFirstPage]);

  const showAllUsers = useCallback(() => {
    fetchUsers(true);
    resetToFirstPage();
  }, [fetchUsers, resetToFirstPage]);

  const saveRiskLevel = useCallback((modalUser, riskData) => {
    setRiskCache((prevCache) => {
      if (riskData.riskLevel === 'none') {
        return Object.fromEntries(
          Object.entries(prevCache).filter(([key]) => key !== modalUser.visorId)
        );
      }

      return {
        ...prevCache,
        [modalUser.visorId]: createRiskCacheEntryFromModal(prevCache[modalUser.visorId], riskData),
      };
    });

    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.visorId === modalUser.visorId
          ? {
              ...user,
              riskLevel: riskData.riskLevel,
              riskData: riskData.riskLevel === 'none' ? null : user.riskData,
            }
          : user
      )
    );
  }, []);

  const saveFavouriteState = useCallback((modalUser, favouriteData) => {
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.visorId === modalUser.visorId
          ? {
              ...user,
              isFavourite: favouriteData.isFavourite,
              isFavourites: favouriteData.isFavourite,
              favouriteNotes: favouriteData.isFavourite ? favouriteData.notes : '',
            }
          : user
      )
    );
  }, []);

  return {
    users,
    sortedUsers,
    paginatedData,
    currentPage,
    totalPages,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    canFetchUsers,
    setCanFetchUsers,
    sortConfig,
    handleSort,
    applyFilters,
    showAllUsers,
    exportToXlsx,
    handlePageChange,
    loadingList,
    errorList,
    userList,
    handleSelectUser,
    saveRiskLevel,
    saveFavouriteState,
  };
}
